import { UseIonRouterResult } from '@ionic/react'
import { GoogleMap } from '@react-google-maps/api'
import { useEffect, useRef, useState } from 'react'
import helpers from '../../../../commonHelpers/helpers'
import { CONST } from '../../../../const/const'
import { MESSAGES_CONST } from '../../../../const/messages-const'
import useToasterHelper from '../../../../helpers/ToasterHelper'
import { CustomError, getCurrentLocation } from '../../../../helpers/helpers'
import useMedia from '../../../../hooks/useMedia'
import { IEventInterface } from '../../../../models/events/event.interface'
import {
  selectMapCenterR,
  selectMapFocusOnSearchedLocationR,
  setMapCenterAc,
  setMapFocusOnSearchedLocationAc,
  setMapFocusedMarkedIdAc,
  setMapLoadingAc,
} from '../../../../store/filters/filterSlice'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import './MapContainer.css'
import MapLoader from './MapLoader'
import Markers from './Markers'
import { useHistory } from 'react-router-dom'

// Constants
const FILE_NAME = 'MapContainer'
const customErrorProps = {
  fileName: FILE_NAME,
  message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
}

// Types
type IUserLocationStatus = 'fulfilled' | 'pending' | 'settled' | 'rejected'

interface IProps {
  isOrganizer?: boolean
}

const MapContainer = (props: IProps) => {
  // Hooks and vars
  const dispatch = useAppDispatch()

  const toastFunction = useToasterHelper()
  const googleMapRef = useRef<GoogleMap>(null)
  const history = useHistory()

  const mapCenter = useAppSelector(selectMapCenterR)
  const isXlResolution = useMedia('(min-width: 1026px)')
  const focusOnSearchedLocation = useAppSelector(selectMapFocusOnSearchedLocationR)

  const [userLocationStatus, setUserLocationStatus] = useState<IUserLocationStatus>('pending')
  const [searchedLocation, setSearchedLocation] = useState<
    typeof CONST.UI.MAP.DEFAULT_LOCATION | null
  >(null)

  const zoomLevel = isXlResolution ? 3 : 1

  useEffect(() => {
    return () => {
      dispatch(setMapFocusedMarkedIdAc(null))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (userLocationStatus === 'settled') {
      setTimeout(() => {
        googleMapRef.current?.state?.map?.setCenter(mapCenter)
        googleMapRef.current?.state?.map?.setZoom(isXlResolution ? 4 : 2)
        setSearchedLocation(mapCenter)
        dispatch(setMapLoadingAc(false))
      }, 300)
    }

    if (userLocationStatus === 'fulfilled') setUserLocationStatus('settled')

    if (userLocationStatus !== 'pending') return

    dispatch(setMapLoadingAc(true))
    getCurrentLocation()
      .then((location) => {
        dispatch(setMapCenterAc(location))
      })
      .catch((error) => {
        helpers.logger({
          message: CustomError.somethingWentWrong({
            ...customErrorProps,
            devMessage: error?.message,
            moduleName: 'useEffect',
          }),
        })
      })
      .finally(() => {
        setUserLocationStatus('fulfilled')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocationStatus])

  useEffect(() => {
    const { lat, lng } = mapCenter

    if (!focusOnSearchedLocation) return

    googleMapRef.current?.state?.map?.setCenter({ lat, lng })
    googleMapRef.current?.state?.map?.setZoom(7)

    dispatch(setMapFocusOnSearchedLocationAc(false))
    setSearchedLocation({ lat, lng })

    return () => {
      dispatch(setMapFocusedMarkedIdAc(null))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusOnSearchedLocation, mapCenter.lat, mapCenter.lng])

  // Functions
  const onEventMarkerClick = (router: UseIonRouterResult, eventId: IEventInterface['id']) => {
    if (!eventId) {
      toastFunction.error({
        message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })

      helpers.logger({
        message: CustomError.somethingWentWrong({
          ...customErrorProps,
          devMessage: `eventId is ${eventId}`,
          moduleName: 'onEventMarkerClick',
        }),
      })

      return
    }

    router.push(`${CONST.ROUTES.EVENT_DETAILS.URL}/${eventId}`)
    history.push(`${CONST.ROUTES.EVENT_DETAILS.URL}/${eventId}`)
  }

  return (
    <div className="flex overflow-x-scroll hide-scroll-bar select-none h-[75vh]">
      <div className="flex flex-wrap w-full transition-all relative">
        <MapLoader />
        {userLocationStatus === 'settled' ? (
          <GoogleMap
            ref={googleMapRef}
            zoom={googleMapRef.current?.state?.map?.getZoom() ?? zoomLevel}
            center={
              googleMapRef.current?.state?.map?.getCenter() ?? {
                lat: mapCenter.lat,
                lng: mapCenter.lng,
              }
            }
            mapContainerClassName="h-[100%] w-full"
            options={{
              minZoom: zoomLevel,
              restriction: {
                latLngBounds: {
                  north: 85,
                  south: -85,
                  west: -180,
                  east: 180,
                },
              },
            }}
          >
            <Markers
              isOrganizers={props.isOrganizer}
              onEventMarkerClick={onEventMarkerClick}
              searchedLocation={searchedLocation}
            />
          </GoogleMap>
        ) : null}
      </div>
    </div>
  )
}

export default MapContainer
